import React, { FC, ReactNode, useEffect, useMemo, useRef } from "react";
import { FieldCheckbox } from "../../fields/FieldCheckbox";
import { Alert } from "../../alerts/Alert";
import {
  getMapFromCustomText,
  TemplateAsComponent,
} from "../../TemplateAsComponent";
import {
  FormsTosLocalization,
  CustomText,
  OrderTerm,
} from "../../../types/graphql/generated";
import { scrollTo } from "../../../helpers/scroll";
import { colors } from "../../../styles/variables";
import { StyledBody, StyledTerm, StyledTerms } from "./index.styled";

interface TermsAndConditionsProps {
  children?: ReactNode;
  formErrors?: Record<string, string>;
  localization?: FormsTosLocalization | null;
  orderTerms?: OrderTerm[];
  submitFailed?: boolean;
  terms: CustomText[];
  type: "info" | "optin";
  valid?: boolean;
}

interface GetSortedTermsByLocation {
  location: "review" | "signup" | "login";
  customTexts: CustomText[];
  hasGasExchangeItems?: boolean;
}

export const getSortedTermsByLocation = ({
  location,
  customTexts,
  hasGasExchangeItems,
}: GetSortedTermsByLocation) => {
  return customTexts
    .filter((customText) => {
      // if has gas exchange items show gas exchange terms
      if (hasGasExchangeItems && customText.type === "gasExchange") return true;
      return customText.type === "terms" && customText.location === location;
    })
    .sort((a, b) => (a.position || 0) - (b.position || 0));
};

const Term: FC<{ term: CustomText; withCheckboxes?: boolean }> = ({
  term,
  withCheckboxes,
}) => {
  return (
    <StyledBody withCheckboxes={withCheckboxes}>
      {term.body.map((section, i) => {
        return (
          <StyledTerm location={term.location} index={i} key={section}>
            <TemplateAsComponent
              template={section}
              map={getMapFromCustomText({
                customText: term,
                underlineLinks: true,
              })}
            />
          </StyledTerm>
        );
      })}
    </StyledBody>
  );
};

export const TermsAndConditions: FC<TermsAndConditionsProps> = (props) => {
  const { children, formErrors, terms, type, orderTerms = [] } = props;
  const hasRequiredCheckboxes =
    type === "optin" || orderTerms.some((t) => t.hasCheckbox && t.isRequired);
  const termsRef = useRef<HTMLDivElement>(null);
  const submitFailed = hasRequiredCheckboxes ? props.submitFailed : false;
  const valid = hasRequiredCheckboxes ? props.valid : true;
  const localization = hasRequiredCheckboxes ? props.localization : null;

  useEffect(() => {
    if (submitFailed && !valid) scrollTo({ ref: termsRef });
  }, [submitFailed, valid]);
  const {
    errorText = "You must read and agree to the terms of service before proceeding",
  } = localization || {};

  const alertErrors = useMemo(() => {
    const errors = [
      ...(type === "optin"
        ? terms.filter((term) => formErrors?.[term.type]).map(() => errorText)
        : []),
      ...orderTerms
        .filter((term) => term.hasCheckbox && formErrors?.[term.type])
        .map((term) => formErrors?.[term.type] || term.errorText),
    ];

    return errors.length > 0 ? errors : [errorText];
  }, [errorText, formErrors, orderTerms, terms, type]);

  return (
    <StyledTerms
      withCheckboxes={hasRequiredCheckboxes}
      ref={hasRequiredCheckboxes ? termsRef : undefined}
    >
      {submitFailed &&
        !valid &&
        alertErrors.map((text, i) => (
          <Alert type="error" bold key={`${text}-${i}`}>
            {text}
          </Alert>
        ))}
      {/* Tenant-configured legal terms */}
      {terms.map((term) =>
        type === "optin" ? (
          <FieldCheckbox
            key={term.type}
            label={<Term term={term} withCheckboxes />}
            name={term.type}
            color={colors.gray600}
            fontSize="13px"
          />
        ) : (
          <Term term={term} key={term.type} />
        )
      )}
      {/* Order-specific terms from the checkout session */}
      {orderTerms.map((term) =>
        term.hasCheckbox ? (
          <FieldCheckbox
            key={term.type}
            label={<StyledBody withCheckboxes>{term.displayText}</StyledBody>}
            name={term.type}
            color={colors.gray600}
            fontSize="13px"
          />
        ) : (
          <StyledBody key={term.type}>{term.displayText}</StyledBody>
        )
      )}
      {children}
    </StyledTerms>
  );
};
